import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import DataTable from "../../components/common/DataTable";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import blService from "../../services/blService";
import reportService from "../../services/reportService";

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function getBLId(bl) {
  return bl.blId || bl.id;
}

function getBLNo(bl) {
  return bl.blNo || bl.blNumber || bl.billOfLadingNo || `BL ${getBLId(bl)}`;
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function BLWiseReportPage() {
  const navigate = useNavigate();
  const { blId } = useEncryptedParams();
  const [selectedBLId, setSelectedBLId] = useState(blId || "");
  const [activeBLs, setActiveBLs] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBLsLoading, setIsBLsLoading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setSelectedBLId(blId || "");
  }, [blId]);

  const loadActiveBLs = useCallback(async () => {
    setIsBLsLoading(true);
    try {
      const data = await blService.getActiveBLs();
      setActiveBLs(data.items || []);
    } catch (err) {
      setError(err?.message || "Failed to load active BL details.");
    } finally {
      setIsBLsLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (currentBLId) => {
    setIsLoading(true);
    setError("");
    try {
      const report = await reportService.getBLWiseReport(currentBLId);
      setRows(report.items);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load BL-wise report.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActiveBLs();
  }, [loadActiveBLs]);

  useEffect(() => {
    loadReport(blId);
  }, [blId, loadReport]);

  const blOptions = useMemo(() => {
    const options = activeBLs.map((bl) => {
      const id = getBLId(bl);
      return {
        value: String(id),
        label: getBLNo(bl),
      };
    });

    return [{ value: "", label: "All active BLs" }, ...options];
  }, [activeBLs]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, item) => ({
          originalWeight: acc.originalWeight + toNumber(item.originalWeight),
          processedWeight: acc.processedWeight + toNumber(item.processedWeight),
          excessWeight: acc.excessWeight + toNumber(item.excessWeight),
          wastageWeight: acc.wastageWeight + toNumber(item.wastageWeight),
          remainingWeight: acc.remainingWeight + toNumber(item.remainingWeight),
        }),
        { 
          originalWeight: 0, 
          processedWeight: 0, 
          excessWeight: 0, 
          wastageWeight: 0, 
          remainingWeight: 0 
        }
      ),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        key: "blNo",
        label: "BL No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white truncate block max-w-[80px] sm:max-w-none">{item.blNo || "-"}</span>
        ),
      },
      { 
        key: "containerNo", 
        label: "Container", 
        sortable: true,
        className: "truncate max-w-[100px] sm:max-w-none"
      },
      { 
        key: "yard", 
        label: "Yard", 
        sortable: true,
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell truncate max-w-[100px]"
      },
      { 
        key: "lot", 
        label: "Lot", 
        sortable: true,
        headerClassName: "hidden sm:table-cell",
        className: "hidden sm:table-cell"
      },
      { 
        key: "category", 
        label: "Category", 
        sortable: true,
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell truncate max-w-[120px]",
        render: (item) => {
          const raw = item.category || "-";
          const categories = String(raw)
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);

          if (categories.length === 0) return "-";
          if (categories.length === 1) {
            return (
              <span className="inline-flex max-w-full items-center truncate rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {categories[0]}
              </span>
            );
          }

          return (
            <div className="flex flex-wrap gap-1">
              {categories.map((category, index) => (
                <span
                  key={`${category}-${index}`}
                  className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  {category}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: "originalWeight",
        label: "Original",
        sortable: true,
        headerClassName: "text-right hidden sm:table-cell",
        className: "hidden sm:table-cell text-right",
        render: (item) => <span className="block w-full">{formatWeight(item.originalWeight)}</span>,
      },
      {
        key: "processedWeight",
        label: "Processed",
        sortable: true,
        headerClassName: "text-right hidden xl:table-cell",
        className: "hidden xl:table-cell text-right",
        render: (item) => <span className="block w-full">{formatWeight(item.processedWeight)}</span>,
      },
      {
        key: "excessWeight",
        label: "Excess",
        sortable: true,
        headerClassName: "text-right hidden xl:table-cell",
        className: "hidden xl:table-cell text-right",
        render: (item) => <span className="block w-full">{formatWeight(item.excessWeight)}</span>,
      },
      {
        key: "wastageWeight",
        label: "Wastage",
        sortable: true,
        headerClassName: "text-right hidden xl:table-cell",
        className: "hidden xl:table-cell text-right",
        render: (item) => <span className="block w-full">{formatWeight(item.wastageWeight)}</span>,
      },
      {
        key: "remainingWeight",
        label: "Remaining",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="block w-full font-bold text-primary-700 dark:text-primary-300">
            {formatWeight(item.remainingWeight)}
          </span>
        ),
      },
    ],
    []
  );

  const handleBLChange = (event) => {
    const value = event.target.value;
    setSelectedBLId(value);
    navigate(value ? `/reports/bl-wise/${encrypt(value)}` : "/reports/bl-wise");
  };

  const handleRefresh = () => {
    loadActiveBLs();
    loadReport(selectedBLId);
  };

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    setError("");
    try {
      const blob = await reportService.downloadBLWiseReport(format, selectedBLId);
      const scope = selectedBLId ? `bl-${selectedBLId}` : "all";
      downloadBlob(blob, `bl-wise-report-${scope}.${format}`);
    } catch (err) {
      setError(err?.message || `Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setDownloadingFormat("");
    }
  };

  return (
    <div className="space-y-4 sm:space-y-3 pb-4 animate-in fade-in duration-500">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-2 sm:pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-600">
            Analytics & Reports
          </p>
          <h1 className="mt-1 text-xl sm:text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight leading-tight">
            BL-wise <span className="text-primary-600">Inventory</span> Report
          </h1>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full lg:w-auto">
          <div className="w-full sm:w-64 lg:w-72">
            <SearchableDropdown
              label="Select Active BL"
              name="blId"
              value={selectedBLId}
              onChange={handleBLChange}
              options={blOptions}
              placeholder={isBLsLoading ? "Loading BLs..." : "All active BLs"}
              disabled={isBLsLoading}
              labelClassName="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1"
            />
          </div>
          <div className="grid grid-cols-3 sm:flex items-center gap-2">
            <Button 
              variant="secondary" 
              size="sm"
              onClick={handleRefresh} 
              isLoading={isLoading || isBLsLoading}
              className="h-10 px-3 sm:px-4 col-span-1"
              title="Refresh Report"
            >
              <i className="material-icons text-sm sm:mr-1.5">refresh</i>
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload("csv")}
              isLoading={downloadingFormat === "csv"}
              disabled={Boolean(downloadingFormat)}
              className="h-10 flex-1 sm:flex-none"
            >
              <i className="material-icons text-sm sm:mr-1.5 text-emerald-600">description</i>
              <span className="text-[10px] sm:text-xs">CSV</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload("pdf")}
              isLoading={downloadingFormat === "pdf"}
              disabled={Boolean(downloadingFormat)}
              className="h-10 flex-1 sm:flex-none"
            >
              <i className="material-icons text-sm sm:mr-1.5 text-rose-500">picture_as_pdf</i>
              <span className="text-[10px] sm:text-xs">PDF</span>
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-rose-600 flex items-center gap-3">
          <i className="material-icons text-sm">error_outline</i>
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            Original Weight
          </p>
          <p className="mt-1 text-base sm:text-lg font-black text-slate-900 dark:text-white font-mono leading-none">
            {formatNumber(totals.originalWeight)} <span className="text-[9px] sm:text-[10px] font-normal text-slate-400">KG</span>
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            Processed Weight
          </p>
          <p className="mt-1 text-base sm:text-lg font-black text-slate-900 dark:text-white font-mono leading-none">
            {formatNumber(totals.processedWeight)} <span className="text-[9px] sm:text-[10px] font-normal text-slate-400">KG</span>
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            Excess Weight
          </p>
          <p className="mt-1 text-base sm:text-lg font-black text-emerald-600 font-mono leading-none">
            {formatNumber(totals.excessWeight)} <span className="text-[9px] sm:text-[10px] font-normal text-slate-400">KG</span>
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            Wastage Weight
          </p>
          <p className="mt-1 text-base sm:text-lg font-black text-rose-500 font-mono leading-none">
            {formatNumber(totals.wastageWeight)} <span className="text-[9px] sm:text-[10px] font-normal text-slate-400">KG</span>
          </p>
        </div>
        <div className="rounded-2xl border border-primary-100 bg-primary-50/50 p-3 sm:p-4 shadow-sm dark:border-primary-900/20 dark:bg-primary-900/10 transition-all hover:shadow-md">
          <p className="text-[9px] font-black uppercase tracking-widest text-primary-600">
            Remaining Weight
          </p>
          <p className="mt-1 text-base sm:text-lg font-black text-primary-700 dark:text-primary-300 font-mono leading-none">
            {formatNumber(totals.remainingWeight)} <span className="text-[9px] sm:text-[10px] font-normal text-primary-400">KG</span>
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          isLoading={isLoading}
          searchPlaceholder="Search BL report..."
          pageSize={10}
          scrollHeight="calc(100vh - 450px)"
        />
      </div>
    </div>
  );
}

export default BLWiseReportPage;
